import React, { useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
//import { SafeAreaView } from 'react-native-safe-area-context';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import WebView from 'react-native-webview';

interface InAppBrowserModalProps {
  visible: boolean;
  url: string;
  title?: string;
  onClose: () => void;
}

const EXTERNAL_SCHEME = /^(tel:|mailto:|sms:|geo:|maps:|market:|itms-apps:|intent:)/i;

export const InAppBrowserModal: React.FC<InAppBrowserModalProps> = ({
  visible,
  url,
  title = 'Website',
  onClose,
}) => {
  const webRef = useRef<any>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [loading, setLoading] = useState(true);

  const initialUrl = useMemo(() => url || 'about:blank', [url]);

  const openExternal = (targetUrl: string) => {
    if (!targetUrl) return;
    Linking.openURL(targetUrl).catch(() => {});
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <SafeAreaProvider>
        <SafeAreaView style={styles.root}>
          <View style={styles.header}>
            <TouchableOpacity style={styles.headerBtn} onPress={onClose}>
              <Text style={styles.headerBtnText}>Close</Text>
            </TouchableOpacity>
            <Text style={styles.title} numberOfLines={1}>{title}</Text>
          <View style={styles.headerRight}>
            <TouchableOpacity
              style={[styles.headerBtn, !canGoBack && styles.headerBtnDisabled]}
              onPress={() => webRef.current?.goBack?.()}
              disabled={!canGoBack}
            >
              <Text style={styles.headerBtnText}>Back</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerBtn} onPress={() => openExternal(currentUrl)}>
              <Text style={styles.headerBtnText}>Open</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.webWrap}>
          <WebView
            ref={webRef}
            source={{ uri: initialUrl }}
            onLoadStart={() => setLoading(true)}
            onLoadEnd={() => setLoading(false)}
            onNavigationStateChange={(state) => {
              setCanGoBack(state.canGoBack);
              setCurrentUrl(state.url);
            }}
            onShouldStartLoadWithRequest={(req) => {
              const nextUrl = req?.url ?? '';
              if (EXTERNAL_SCHEME.test(nextUrl)) {
                openExternal(nextUrl);
                return false;
              }
              return true;
            }}
            setSupportMultipleWindows={false}
          />
          {loading && (
            <View style={styles.loading}>
              <ActivityIndicator size="small" color="#3498db" />
            </View>
          )}
        </View>
      </SafeAreaView>
      </SafeAreaProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    minHeight: 52,
    backgroundColor: '#1f2937',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    gap: 8,
  },
  title: {
    flex: 1,
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  headerRight: {
    flexDirection: 'row',
    gap: 6,
  },
  headerBtn: {
    backgroundColor: '#374151',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  headerBtnDisabled: {
    opacity: 0.45,
  },
  headerBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  webWrap: {
    flex: 1,
  },
  loading: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
});
